<template>
    <!-- "Improve with AI" — the one AI button on every editor (Keelan, 2 Nov 2026).
         Three ways in, each explained: chat about the changes, a full tune-up, or (processes)
         an update from a recording. Chat agrees a plan (/api/ai/improve-chat); "Make these
         changes" applies only that plan to what's on screen (/api/ai/revise) and opens the
         normal review, where nothing reaches the editor until Apply. -->
    <div>
        <!-- Wide screens in an editor: a panel on the right that stays put while the page scrolls, so the page can be read
             while chatting (Keelan, 3 Nov). It takes the Outline/Activity column's place and the left menu folds away.
             Phones and narrow windows keep the pop-up. -->
        <div v-show="!sub" :class="docked ? 'aip-dock-wrap' : 'error-msg-wrap'" :style="docked ? { top: dockTop + 'px' } : undefined">
            <div :class="docked ? 'aip aip-docked' : 'form-wrapper ai-modal aip slide-up'" :role="docked ? 'complementary' : 'dialog'" :aria-modal="docked ? undefined : 'true'" aria-labelledby="aip-title">
                <div class="form-close">
                    <button type="button" class="form-close-x" aria-label="Close" title="Close" @click="close()">&times;</button>
                </div>
                <div class="aip-head">
                    <span class="aip-badge" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg>
                    </span>
                    <div>
                        <div id="aip-title" class="aip-title">Improve with AI</div>
                        <div class="aip-sub">Pick how you want help. Nothing changes in your {{ noun }} until you've seen the result and pressed Apply.</div>
                    </div>
                </div>

                <div :class="['aip-options', { compact: messages.length && choice == 'chat', brief: docked }]" role="radiogroup" aria-label="How should the AI help?">
                    <button v-for="o in options" :key="o.key" type="button" role="radio" :aria-checked="choice == o.key" :class="['aip-opt', { on: choice == o.key }]" @click="choose(o.key)">
                        <span class="aip-opt-ic" v-html="o.icon"></span>
                        <span class="aip-opt-txt">
                            <strong>{{ o.title }}</strong>
                            <span>{{ o.text }}</span>
                            <em>{{ o.best }}</em>
                        </span>
                    </button>
                </div>

                <!-- CHAT -->
                <section v-if="choice == 'chat'" class="aip-chat" aria-labelledby="aip-chat-title">
                    <!-- Says what this box is (Nate/Keelan, 3 Nov): it belongs to "Tell it what to change" -->
                    <div class="aip-chat-head">
                        <span class="aip-chat-ic" aria-hidden="true" v-html="chatIcon"></span>
                        <div>
                            <h3 id="aip-chat-title" class="aip-chat-title">Chat with the AI</h3>
                            <p class="aip-chat-sub">Tell it what to change in this {{ noun }}. It keeps a list as you go, and nothing changes until you press <strong>Make these changes</strong>.</p>
                        </div>
                    </div>
                    <div ref="log" class="aip-log" aria-live="polite">
                        <div class="aip-msg ai">
                            <span class="aip-who">AI</span>
                            <div>What would you like to change? Say it the way you'd tell a colleague. For example: <em>{{ example }}</em></div>
                        </div>
                        <div v-for="(m, i) in messages" :key="i" :class="['aip-msg', m.role == 'assistant' ? 'ai' : 'me']">
                            <span class="aip-who">{{ m.role == 'assistant' ? 'AI' : 'You' }}</span>
                            <div>{{ m.text }}</div>
                        </div>
                        <div v-if="thinking" class="aip-msg ai aip-typing"><span class="aip-who">AI</span><div><i></i><i></i><i></i></div></div>
                    </div>

                    <div v-if="plan.length" class="aip-plan">
                        <div class="aip-plan-h">Changes to make ({{ plan.length }})</div>
                        <ul><li v-for="(p, i) in plan" :key="i">{{ p }}</li></ul>
                    </div>

                    <div v-if="error" class="aip-error" role="alert">{{ error }}</div>

                    <!-- Side panel: the changes go straight onto the page, which is the review. Keep or Undo; nothing is saved until Save. -->
                    <div v-if="applied" class="aip-applied" role="status">
                        <div class="aip-applied-h">Changes made on the page</div>
                        <ul v-if="appliedSummary.length"><li v-for="(c, i) in appliedSummary" :key="i">{{ c }}</li></ul>
                        <p>Look them over on the page. Nothing is saved until you press <strong>Save</strong>.</p>
                        <div class="aip-applied-acts">
                            <button type="button" class="ai-btn" @click="keep()">Keep</button>
                            <button type="button" class="ai-btn ai-btn-ghost" @click="undo()">Undo</button>
                        </div>
                    </div>

                    <div v-if="!applied" class="aip-input">
                        <textarea ref="input" v-model="text" rows="2" :disabled="applying" placeholder="Type what you want changed, or tap the mic and talk…" aria-label="Message to the AI" @keydown.enter.exact.prevent="send()"></textarea>
                        <ai-dictation class="aip-mic" @text="text = (text ? text.trim() + ' ' : '') + $event" />
                        <button type="button" class="aip-send" :disabled="!text.trim() || thinking || applying" aria-label="Send" title="Send" @click="send()">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                        </button>
                    </div>

                    <div v-if="!applied" class="aip-foot">
                        <span class="aip-hint">{{ applying ? 'Making the changes… this usually takes 20–40 seconds.' : canApply ? 'Ready when you are. You\'ll see the result before anything changes.' : 'Tell the AI what to change, then press Make these changes.' }}</span>
                        <button type="button" :class="['ai-btn', { 'disable-button': !canApply || applying || thinking }]" :disabled="!canApply || applying || thinking" @click="makeChanges()">
                            <span v-if="applying" class="aip-spin" aria-hidden="true"></span>
                            {{ applying ? 'Making changes…' : 'Make these changes' }}
                        </button>
                    </div>
                </section>

                <!-- TUNE-UP / RECORDING -->
                <div v-else class="aip-go">
                    <p v-if="choice == 'tuneup'">The AI reads the whole {{ noun }}<template v-if="nodeType == 'sheet'">, every seat,</template> and suggests a sharper version. You'll see a list of what it changed and can ask for more changes before applying.<template v-if="nodeType != 'sheet'"> It works from the last saved version, so save first if you've made edits you want it to keep.</template></p>
                    <p v-else>Record your screen while you do the work, or upload a video, photos or the written procedure. The AI compares it with this process and drafts the update for you to review.</p>
                    <button type="button" class="ai-btn" @click="sub = choice">{{ choice == 'tuneup' ? 'Start the tune-up' : 'Continue' }}</button>
                </div>
            </div>
        </div>

        <ai-process-modal v-if="sub == 'tuneup'" mode="improve" :node-type="nodeType" :slug="slug" :current-draft="nodeType == 'sheet' ? currentDraft : null" :preview-base-node="previewBaseNode" @close="sub = null" @accept="accept" />
        <ai-process-modal v-if="sub == 'recording'" mode="improve" :video-improve="true" :slug="slug" accept-label="Apply Update to Editor" @close="sub = null" @accept="accept" />
        <ai-process-modal v-if="sub == 'review'" mode="improve" :node-type="nodeType" :slug="slug" :edit-only="true" :initial-draft="revised" :initial-note="feedbackText()" :preview-base-node="previewBaseNode" @close="sub = null" @accept="accept" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AIDictation from '@/components/AIDictation.vue';
import AIProcessModal from '@/components/AIProcessModal.vue';

const ICONS = {
    chat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 9h8M8 13h5"/></svg>',
    tuneup: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg>',
    recording: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M15 8v8H5V8h10zm2 2.5V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4.5l5 4v-11l-5 4z"/></svg>'
};

const DOCK_MIN_WIDTH = 900;
const NOUNS: any = { process: 'process', checklist: 'Powerlist', sheet: 'role sheet', timeline: 'value engine' };
const EXAMPLES: any = {
    process: '"Add a step for photographing the delivery before signing, and make the safety warning in step 3 stronger."',
    checklist: '"Add a check for tire pressure before the trip, and split the paperwork task into two."',
    sheet: '"Give the Office Manager seat the weekly payroll duties, and add a Recurring Meetings section to every seat."',
    timeline: '"Add a quality-check step after production, and say the shipping step takes 1 to 2 days."'
};

export default defineComponent({
    name: 'AIImprovePanel',
    components: {
        AiDictation: AIDictation,
        AiProcessModal: AIProcessModal
    },
    // nodeType: process | checklist | sheet | timeline. currentDraft: what's on screen now, in the
    // shape AIProcessModal drafts use ({ name, items } or { name, sheet }). accept(draft, jobId) is
    // the same event AIProcessModal emits, so editors keep their existing onAIDraft handler.
    props: ['nodeType', 'slug', 'currentDraft', 'previewBaseNode'],
    emits: ['close', 'accept', 'apply'],
    data() {
        return {
            choice: 'chat',
            docked: false,     // side panel (wide screens, inside an editor) instead of the pop-up
            applied: false,    // side panel: changes are on the page, waiting for Keep or Undo
            appliedSummary: [] as string[],
            before: null as any, // what the page held before those changes (for Undo)
            dockTop: 0,        // just under the editor bar, which stays at the top while the page scrolls
            sub: null as null | string,
            messages: [] as { role: string; text: string }[],
            plan: [] as string[],
            text: '',
            thinking: false,
            applying: false,
            error: '',
            revised: null as any
        };
    },
    computed: {
        noun(): string {
            return NOUNS[this.nodeType] || 'process';
        },
        example(): string {
            return EXAMPLES[this.nodeType] || EXAMPLES.process;
        },
        options(): any[] {
            const list = [
                { key: 'chat', icon: ICONS.chat, title: 'Tell it what to change', text: `Chat about the changes you want, typed or spoken. The AI makes only those changes, to the ${this.noun} on your screen, including edits you haven't saved.`, best: 'Best when you know what you want different.' },
                { key: 'tuneup', icon: ICONS.tuneup, title: 'Give it a full tune-up', text: `The AI reviews the whole ${this.noun} against your company's rules and suggests a sharper version: clearer wording, missing details, who owns what.`, best: 'Best for an old or rough one.' }
            ];
            if (this.nodeType == 'process' || !this.nodeType) list.push({ key: 'recording', icon: ICONS.recording, title: 'Update from a recording', text: 'Show how the job is done today: record your screen, or upload a video, photos or the written procedure. The AI compares it with this process.', best: 'Best when the way you work has changed.' });
            return list;
        },
        chatIcon(): string { return ICONS.chat; },
        canApply(): boolean {
            return this.plan.length > 0 || !!this.text.trim() || this.messages.some(m => m.role == 'user');
        }
    },
    mounted() {
        this.placeDock();
        window.addEventListener('resize', this.placeDock);
        this.$nextTick(() => (this.$refs.input as any)?.focus?.());
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.placeDock);
        this.setDocked(false);
    },
    methods: {
        // Docked from 900px wide when opened inside an editor (.ep); the pop-up otherwise
        placeDock() {
            const bar = document.querySelector('.ep > .page-bar, .ep .page-bar') as HTMLElement | null;
            const dock = window.innerWidth >= DOCK_MIN_WIDTH && !!document.querySelector('.ep');
            if (dock) this.dockTop = Math.max(0, Math.round(bar ? bar.getBoundingClientRect().bottom : 0));
            this.setDocked(dock);
        },
        // While docked: the editor makes room (Outline/Activity hidden, see the unscoped styles) and the app
        // folds the left menu (App.vue watches aiDocked and puts it back afterwards)
        setDocked(on: boolean) {
            this.docked = on;
            document.body.classList.toggle('aip-docked-on', on);
            if (this.$store.state.aiDocked !== on) this.$store.state.aiDocked = on;
        },
        choose(key: string) {
            this.choice = key;
            if (key == 'chat') this.$nextTick(() => (this.$refs.input as any)?.focus?.());
        },
        close() {
            if (this.applying) return;
            this.$emit('close');
        },
        scrollLog() {
            this.$nextTick(() => {
                const log = this.$refs.log as any;
                if (log) log.scrollTop = log.scrollHeight;
            });
        },
        async send() {
            const text = this.text.trim();
            if (!text || this.thinking || this.applying) return;
            this.error = '';
            this.messages.push({ role: 'user', text });
            this.text = '';
            this.thinking = true;
            this.scrollLog();
            try {
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/ai/improve-chat`, {
                    nodeType: this.nodeType, slug: this.slug, currentDraft: this.currentDraft, messages: this.messages
                });
                this.messages.push({ role: 'assistant', text: resp.data.reply || 'Got it.' });
                this.plan = resp.data.plan || [];
            } catch (e: any) {
                this.error = (typeof e?.response?.data == 'string' && e.response.data) || 'The AI could not answer just now. Please try again.';
            }
            this.thinking = false;
            this.scrollLog();
        },
        // What /revise applies: the agreed plan, plus the conversation for context
        feedbackText(): string {
            const pending = this.text.trim();
            const plan = this.plan.length ? this.plan : this.messages.filter(m => m.role == 'user').map(m => m.text);
            const parts: string[] = [];
            if (plan.length) parts.push('Changes to make:\n' + plan.map(p => '- ' + p).join('\n'));
            if (pending) parts.push('Also: ' + pending);
            if (this.messages.length > 1) parts.push('Conversation, for context only:\n' + this.messages.map(m => (m.role == 'assistant' ? 'AI: ' : 'User: ') + m.text).join('\n').slice(-12000));
            return parts.join('\n\n');
        },
        async makeChanges() {
            if (!this.canApply || this.applying || this.thinking) return;
            this.error = '';
            this.applying = true;
            try {
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/ai/revise`, {
                    nodeType: this.nodeType, slug: this.slug, previousDraft: this.currentDraft, feedback: this.feedbackText()
                });
                this.revised = resp.data;
                if (this.docked) {
                    // The page is the review: apply now, keep a copy of what was there for Undo
                    this.before = JSON.parse(JSON.stringify(this.currentDraft));
                    this.$emit('apply', resp.data);
                    this.appliedSummary = (resp.data && resp.data.changesSummary) || [];
                    this.applied = true;
                } else this.sub = 'review';
            } catch (e: any) {
                this.error = (typeof e?.response?.data == 'string' && e.response.data) || 'Could not make those changes. Your editor is unchanged; please try again.';
            }
            this.applying = false;
        },
        accept(...args: any[]) {
            this.$emit('accept', ...args);
        },
        // Keep: the changes stay on the page (still unsaved); start a fresh request from what's there now
        keep() {
            this.applied = false; this.before = null; this.plan = []; this.text = '';
            this.messages = [ { role: 'assistant', text: `Kept. Press Save when you're happy with the ${this.noun}, or tell me what else to change.` } ];
            this.scrollLog();
            this.$nextTick(() => (this.$refs.input as any)?.focus?.());
        },
        // Undo: put the page back exactly as it was; the list of changes stays so it can be adjusted and tried again
        undo() {
            if (this.before) this.$emit('apply', this.nodeType == 'timeline' ? { ...this.before, preserved: true } : this.before);
            this.applied = false; this.before = null;
            this.messages.push({ role: 'assistant', text: `Undone. The ${this.noun} is back to how it was. Change the list and press Make these changes to try again.` });
            this.scrollLog();
        }
    }
});
</script>

<style scoped>
.aip { width: 720px; max-width: calc(100vw - 24px); box-sizing: border-box; }
/* Docked: fixed on the right under the editor bar; scrolls on its own, the chat fills the height */
.aip-dock-wrap { position: fixed; right: 0; bottom: 0; width: var(--aip-dock-w); z-index: 950; }
.aip.aip-docked { position: relative; width: 100%; max-width: none; height: 100%; overflow-y: auto; display: flex; flex-direction: column;
    background: #fff; border-left: 1px solid #eadfd5; box-shadow: -16px 0 36px -20px rgba(44, 41, 38, .35); padding: 18px 18px 20px; animation: aip-dock-in .18s ease-out; }
@keyframes aip-dock-in { from { transform: translateX(24px); } to { transform: none; } }
@media (prefers-reduced-motion: reduce) { .aip.aip-docked { animation: none; } }
.aip-docked .form-close { position: absolute; top: 10px; right: 10px; }
.aip-docked .aip-head { margin-right: 30px; margin-bottom: 12px; }
.aip-docked .aip-title { font-size: 18px; }
.aip-docked .aip-options { grid-template-columns: 1fr; gap: 8px; margin-bottom: 12px; }
.aip-docked .aip-options.compact { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
.aip-docked .aip-options.compact .aip-opt-txt strong { font-size: 12.5px; line-height: 1.25; }
.aip-options.brief .aip-opt { padding: 10px 12px; }
.aip-options.brief .aip-opt-txt > span { display: none; }
.aip-docked .aip-chat { flex: 1 1 auto; display: flex; flex-direction: column; min-height: 320px; }
.aip-docked .aip-log { flex: 1 1 auto; max-height: none; min-height: 120px; }
.aip-applied { margin-top: 10px; border: 1px solid #b7e4c7; background: #f3fbf6; border-radius: 12px; padding: 12px 14px; display: grid; gap: 8px; }
.aip-applied-h { font-size: 11.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #1b7a3d; }
.aip-applied ul { margin: 0; padding-left: 18px; display: grid; gap: 3px; font-size: 13.5px; color: #2b221b; }
.aip-applied p { margin: 0; font-size: 13px; color: #4b4138; }
.aip-applied-acts { display: flex; gap: 8px; }
.aip-head { display: flex; align-items: flex-start; gap: 14px; margin: 0 28px 16px 0; }
.aip-badge { width: 38px; height: 38px; min-width: 38px; border-radius: 11px; background: var(--ai-grad); color: #fff; display: inline-flex; align-items: center; justify-content: center; box-shadow: var(--ai-shadow); }
.aip-badge svg { width: 19px; height: 19px; }
.aip-title { font-size: 21px; font-weight: 700; color: #1f1712; }
.aip-sub { color: #6f655c; font-size: 13.5px; margin-top: 3px; line-height: 1.45; }

.aip-options { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 10px; margin-bottom: 16px; }
.aip-opt { display: flex; gap: 10px; align-items: flex-start; text-align: left; background: #fff; border: 1.5px solid #f1e3d6; border-radius: 12px; padding: 12px; cursor: pointer; font: inherit; color: inherit; transition: border-color .15s, box-shadow .15s, background .15s; }
.aip-opt:hover { border-color: #fdba74; }
.aip-opt:focus-visible { outline: 3px solid rgba(249, 115, 22, .35); outline-offset: 2px; }
.aip-opt.on { border-color: var(--ai-accent); background: #fff8f1; box-shadow: 0 0 0 3px rgba(249, 115, 22, .12); }
.aip-opt-ic { width: 30px; height: 30px; min-width: 30px; border-radius: 9px; background: #fff1e4; color: var(--ai-accent-deep); display: inline-flex; align-items: center; justify-content: center; }
.aip-opt.on .aip-opt-ic { background: var(--ai-grad); color: #fff; }
.aip-opt-ic :deep(svg) { width: 16px; height: 16px; }
.aip-opt-txt { display: flex; flex-direction: column; gap: 3px; font-size: 12.5px; line-height: 1.4; color: #5c5148; }
.aip-opt-txt strong { font-size: 14px; color: #1f1712; }
.aip-opt-txt em { font-style: normal; font-weight: 600; color: var(--ai-accent-deep); }
/* once a chat has started the cards shrink to their titles, so the chat and its buttons stay in view */
.aip-options.compact .aip-opt { padding: 8px 10px; align-items: center; }
.aip-options.compact .aip-opt-txt span, .aip-options.compact .aip-opt-txt em { display: none; }
.aip-options.compact .aip-opt-ic { width: 26px; height: 26px; min-width: 26px; }

.aip-chat { border: 1px solid #f1e3d6; border-radius: 14px; background: #fffcf9; padding: 12px; }
.aip-chat-head { display: flex; align-items: flex-start; gap: 10px; padding: 4px 2px 12px; margin-bottom: 10px; border-bottom: 1px solid #f3e6da; }
.aip-chat-ic { width: 32px; height: 32px; min-width: 32px; border-radius: 9px; background: var(--ai-grad); color: #fff; display: inline-flex; align-items: center; justify-content: center; }
.aip-chat-ic :deep(svg) { width: 17px; height: 17px; }
.aip-chat-title { margin: 0; font-size: 16.5px; font-weight: 700; color: #1f1712; line-height: 1.3; }
.aip-chat-sub { margin: 2px 0 0; font-size: 13.5px; color: #6f655c; line-height: 1.45; }
.aip-chat-sub strong { color: #3a2f27; font-weight: 600; }
.aip-log { max-height: min(38vh, 360px); overflow-y: auto; display: flex; flex-direction: column; gap: 10px; padding: 2px 2px 8px; }
.aip-msg { display: flex; gap: 8px; align-items: flex-start; font-size: 14px; line-height: 1.5; color: #2b221b; max-width: 92%; }
.aip-msg > div { background: #fff; border: 1px solid #f3e6da; border-radius: 12px; padding: 8px 12px; white-space: pre-wrap; }
.aip-msg.me { align-self: flex-end; flex-direction: row-reverse; }
.aip-msg.me > div { background: #fff1e4; border-color: #fdd9bb; }
.aip-who { font-size: 11px; font-weight: 700; color: #a0958a; padding-top: 9px; min-width: 22px; }
.aip-typing i { display: inline-block; width: 6px; height: 6px; margin: 0 2px; border-radius: 50%; background: #f59e0b; animation: aip-dot 1s infinite ease-in-out; }
.aip-typing i:nth-child(2) { animation-delay: .15s; }
.aip-typing i:nth-child(3) { animation-delay: .3s; }
@keyframes aip-dot { 0%, 80%, 100% { opacity: .25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }

.aip-plan { background: var(--ai-grad-soft); border: 1px solid var(--ai-border-soft); border-radius: 12px; padding: 8px 14px; margin: 4px 0 10px; }
.aip-plan-h { font-size: 11.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--ai-accent-deep); }
.aip-plan ul { margin: 6px 0 2px; padding-left: 20px; font-size: 13.5px; }
.aip-plan li { margin-bottom: 3px; }

.aip-error { background: #fdecea; border: 1px solid #f5c6c3; color: #9c2b23; padding: 8px 12px; border-radius: 10px; margin: 0 0 10px; font-size: 13.5px; }
.aip-input { position: relative; }
.aip-input textarea { width: 100%; box-sizing: border-box; border: 1px solid var(--ai-border-soft); border-radius: 12px; padding: 11px 92px 11px 12px; font: inherit; font-size: 14px; resize: vertical; min-height: 52px; background: #fff; }
.aip-input textarea:focus { outline: none; border-color: #fdba74; box-shadow: 0 0 0 3px rgba(249, 115, 22, .15); }
.aip-mic { position: absolute; right: 50px; bottom: 11px; }
.aip-send { position: absolute; right: 8px; bottom: 8px; width: 36px; height: 36px; border-radius: 10px; border: none; background: var(--ai-grad); color: #fff; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.aip-send:disabled { opacity: .4; cursor: default; }
.aip-send svg { width: 18px; height: 18px; }

.aip-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 10px; flex-wrap: wrap; }
.aip-hint { font-size: 12.5px; color: #7a6f65; flex: 1 1 220px; }
.aip-spin { width: 14px; height: 14px; border-radius: 50%; border: 2px solid rgba(255, 255, 255, .45); border-top-color: #fff; animation: aip-rot .8s linear infinite; }
@keyframes aip-rot { to { transform: rotate(360deg); } }

.aip-go { border: 1px solid #f1e3d6; border-radius: 14px; background: #fffcf9; padding: 14px 16px; display: flex; flex-direction: column; align-items: flex-start; gap: 10px; }
.aip-go p { margin: 0; font-size: 14px; line-height: 1.5; color: #4a3f36; }

@media (max-width: 640px) {
    .aip { padding: 18px 14px !important; }
    .aip-options { grid-template-columns: 1fr; gap: 8px; }
    .aip-options.compact { grid-template-columns: repeat(3, 1fr); gap: 6px; }
    .aip-options.compact .aip-opt-txt strong { font-size: 12px; line-height: 1.25; }
    .aip-options.compact .aip-opt-ic { display: none; }
    .aip-opt { padding: 10px; }
    .aip-opt-txt em { display: none; }
    .aip-log { max-height: 34vh; }
    .aip-msg { max-width: 100%; }
}
</style>

